import { buildingService } from '@/services/api'
import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref } from 'vue'
import { useAlertStore } from '../system'
import { useItem } from '@/composables/item'
import { useTeamStore } from './team'
import { isEqual } from 'lodash'

type Status = 'initial' | 'ready' | 'loading' | 'error'

export const useBuildingStore = defineStore('building', () => {
	const status = ref<Status>('initial')
	const data = ref<Api.Building[]>()
	const dataInit = ref<Api.Building[]>()
	const meta = ref<Api.CollectionMeta>()

	const { items, show, update, remove, getItem, itemAdd } = useItem<
		Api.Building,
		Api.BuildingPostInput,
		Api.BuildingUpdateInput
	>(buildingService, 'buildings', 'Building', {
		// loadModules removed - propertymanager.person is now loaded via rel parameter in buildingService.show()
	})

	function getItemModule(id: number, module: any) {
		items.value.find((item) => item.id === id)[module]
	}

	function dataSet(value) {
		data.value = value
	}
	function dataInitSet(value) {
		dataInit.value = value
	}
	function imageAdd({ id, image }: { id: number; image: any }) {
		items.value.find((item) => item.id === parseInt(id))?.images.push(image)
	}
	function imageRemove({ buildingId, imageId }: Record<string, number>) {
		const building = items.value.find((item) => item.id === buildingId)
		const index = building?.images.findIndex(
			(image) => image.image_id === imageId || image.id === imageId
		)
		if (index && index >= 0) {
			building?.images?.splice(index, 1)
		}
	}
	function metaSet(value) {
		meta.value = value
	}
	function statusSet(value: Status) {
		status.value = value
	}

	async function load() {
		status.value = 'loading'
		try {
			const { data } = await buildingService.index()
			if (typeof data === 'undefined') return
			dataInit.value = data.data
			meta.value = data.meta
			status.value = 'ready'
			return data.data
		} catch (error) {
			status.value = 'error'
			useAlertStore().errorAlert(error)
			return error
		}
	}
	function loadAccounts(id: number, role?: number) {
		return buildingService.accountsGet(id, role).then(
			({ data }) => {
				return data.data
			},
			(error) => {
				status.value = 'error'
				useAlertStore().errorAlert(error)
				return error
			}
		)
	}
	function loadItemModule({ id, module }: { id: number; module: string }) {
		const building = getItem(id)
		if (typeof building === 'undefined') {
			show({ id }).then(() => {
				loadItemModule({
					id,
					module,
				})
			})
			return
		}
		if (typeof building[module] !== 'undefined') {
			return building[module]
		}
		status.value = 'loading'
		return buildingService.itemModuleGet(id, module).then(
			({ data }) => {
				building[module] = data
				itemAdd(building)
				statusSet('ready')
				return data
			},
			(error) => {
				statusSet('error')
				useAlertStore().errorAlert(error)
				return error
			}
		)
	}
	function setTeamMember({ id, team }: { id: number; team: number }) {
		statusSet('loading')
		return buildingService.update(id, { data: { team_id: team } }).then(
			({ data: building }) => {
				const storedBuilding = getItem(id)
				if (storedBuilding) {
					building.propertymanager = storedBuilding.propertymanager
					building.images = storedBuilding.images
				}
				if (team === 0) {
					itemAdd(building)
					statusSet('ready')
					return building
				}
				return useTeamStore()
					.show(id)
					.then((team) => {
						building.team = team
						itemAdd(building)
						statusSet('ready')
						return building
					})
			},
			(error) => {
				statusSet('error')
				useAlertStore().errorAlert(error)
				return error
			}
		)
	}

	function collectionUpdate({ data, init }) {
		if (isEqual(data, init)) {
			return Promise.resolve()
		}

		statusSet('loading')

		return Promise.all(
			data.map((item, i) => {
				if (!isEqual(item, init[i])) {
					const { id, ...updateData } = item
					return buildingService.update(id, { data: updateData }).catch((error) => {
						useAlertStore().errorAlert(error)
						return error
					})
				}
			})
		).finally(() => {
			statusSet('ready')
		})
	}

	function $reset() {
		//
	}
	return {
		status,
		data,
		dataInit,
		meta,
		imageAdd,
		imageRemove,
		load,
		loadAccounts,
		loadItemModule,
		setTeamMember,
		collectionUpdate,
		$reset,
		getItem,
		items,
		show,
		update,
		remove,
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useBuildingStore, import.meta.hot))
}
